import React, { useEffect, useState } from 'react';
import VehicleExpenseModal from '../components/modals/VehicleExpenseModal';
import VehicleExpenseTable from '../components/desktop/VehicleExpenseTable';
import VehicleExpenseFilters from '../components/common/VehicleExpenseFilters';
import VehicleExpenseApprovalTable from '../components/desktop/VehicleExpenseApprovalTable';
import { addExpense, updateVehicleExpensePartial,
  approveVehicleExpense, rejectVehicleExpense, deleteVehicleExpense
} from '../services/vehicle_expenses';
import { toast } from 'react-toastify';
import Spinner from '../components/ui/Spinner';
import { getVehicles } from '../services/vehicles';
import { useBranches } from '../context/branches';
import { USER_PROFILE, USER_ROLE } from '../services/auth';
import { useOverallCount } from '../context/OverallCountContext';

const VehicleExpenses = ({ vehicle, onBack }) => {

  const role = localStorage.getItem(USER_ROLE);
  const profile = JSON.parse(localStorage.getItem(USER_PROFILE) || '{}');
  const showBranches = (role == 'admin');

  const [month, setMonth] = useState(() => new Date().toISOString().slice(5, 7));
  const [year, setYear] = useState(() => new Date().toISOString().slice(0, 4));
  const [status, setStatus] = useState('all');
  const [expenseCount, setExpenseCount] = useState(0);
  const [approvalCount, setApprovalCount] = useState(0);
  const [showModal, setShowModal] = useState(false);
  const [editingExpense, setEditingExpense] = useState(null);
  const [errors, setErrors] = useState([]);
  const [initialData, setInitialData] = useState({});
  const [vehicles, setVehicles] = useState([]);
  const [selectedVehicle, setSelectedVehicle] = useState(vehicle ? vehicle.id : null);
  const [selectedBranch, setSelectedBranch] = useState(showBranches ? vehicle?.branch.id : null);
  const [reload, setReload] = useState(false);
  const [activeTab, setActiveTab] = useState('expenses');
  const [loading, setLoading] = useState(false);
  let branches = [], branch_loading = false, refreshBranches = null;
  if (showBranches) {
    const result = useBranches({ reload: false, params: { status: 'active', paginate: false } });
    branches = result.branches;
    branch_loading = result.loading;
    refreshBranches = result.refresh;
  }
  const { overallCount, loading: overall_count_loading, refresh: refreshOverallCount } = useOverallCount({ reload: false });

  const openedFromList = !!onBack;
  const selectedVehicleObj = vehicles.find((v) => Number(v.id) === Number(selectedVehicle));
  const canAddExpense = !!selectedVehicleObj;
  const branchLabel = showBranches
    ? (branches.find((b) => Number(b.id) === Number(selectedBranch))?.name || '-')
    : (selectedVehicleObj?.branch?.name || profile?.branch?.name || '-');

  useEffect(() => {
    if (!showBranches) {
      fetchVehicles(vehicle?.branch.id);
    }
  }, []);

  useEffect(() => {
    if (showBranches) {
      if (branches?.length > 0) {
        let branch = 0;
        if (vehicle) {
          branch = vehicle.branch?.id;
        } else {
          branch = branches[0].id;
        }
        setSelectedBranch(branch);
        fetchVehicles(branch);
      }
    }
  }, [branches]);

  const fetchVehicles = async (branch) => {
    setLoading(true);
    try {
      let err = null, data = [];
      if (showBranches) {
        [err, data] = await getVehicles({ paginate: false, branch });
      } else {
        [err, data] = await getVehicles({ paginate: false });
      }
      if (err) {
        console.error('Error fetching vehicles:', err);
        toast.error('Failed to load vehicles.');
        setVehicles([]);
        setSelectedVehicle(null);
      } else {
        const list = (data && data.results) ? data.results : (Array.isArray(data) ? data : []);
        setVehicles(list);
        if (vehicle) {
          setSelectedVehicle(vehicle?.id);
        } else {
          if (list.length > 0) {
            setSelectedVehicle(list[0].id);
          } else {
            setSelectedVehicle(null);
          }
        }
      }
    } catch (e) {
      console.error('Fetch vehicles exception:', e);
      toast.error('Failed to load vehicles.');
    } finally {
      setLoading(false);
    }
  };

  const handleAdd = () => {
    if (!canAddExpense) {
      toast.error('Please select a vehicle before adding expense.');
      return;
    }
    setEditingExpense(null);
    setShowModal(true);
    setErrors([]);
  };

  const handleEdit = (expense) => {
    setEditingExpense(expense);
    setInitialData(expense);
    setShowModal(true);
    setErrors([]);
  };

  const handleDelete = async (expense) => {
    const [err, response] = await deleteVehicleExpense(expense.id);
    if (err) {
      toast.error("Failed to delete expense.");
    } else {
      toast.success("Expense deleted successfully.");
      setReload(!reload);
    }
  };

  const getChangedFields = (initial, updated) => {
    const changed = {};
    for (const key in updated) {
      if (Array.isArray(updated[key])) {
        if (JSON.stringify(initial[key]) !== JSON.stringify(updated[key])) {
          changed[key] = updated[key];
        }
      } else if (updated[key] !== initial[key]) {
        changed[key] = updated[key];
      }
    }
    return changed;
  };

  const handleSaved = async (data) => {
    let err, response;
    if (editingExpense) {
      const changedFields = getChangedFields(initialData, data);
      if (Object.keys(changedFields).length === 0) {
        return;
      }
      [err, response] = await updateVehicleExpensePartial(data.id, changedFields);
      if (!err) {
        setInitialData({});
      }
    } else {
      [err, response] = await addExpense(data);
    }
    if (err) {
      setErrors(err);
    } else {
      toast.success("Expense Saved Successfully.");
      setShowModal(false);
      setReload(!reload);
      refreshOverallCount();
    }
  };

  const handleApprove = async (expense) => {
    const [err, response] = await approveVehicleExpense(expense.id);
    if (!err) {
      toast.success("Expense approved successfully.");
      setReload(!reload);
      refreshOverallCount();
    }
  }

  const handleReject = async (expense) => {
    const [err, response] = await rejectVehicleExpense(expense.id);
    if (!err) {
      toast.success("Expense rejected successfully.");
      setReload(!reload);
      refreshOverallCount();
    }
  }

  const updateCount = (tab, count) => {
    if (tab === 'expenses') {
      setExpenseCount(count);
    } else if (tab === 'approvals') {
      setApprovalCount(count);
    }
  }

  const onBranchChange = async (e) => {
    setSelectedBranch(e.target.value);
    await fetchVehicles(e.target.value);
  };

  if (loading) return <Spinner />;

  return (
    <div className="p-4 bg-slate-50 min-h-screen uppercase [&_input[type='text']]:uppercase [&_select]:uppercase">
      <div className="flex justify-between items-center mb-4 gap-3 flex-wrap">
        {!openedFromList && <h2 className="text-xl font-bold uppercase text-[#293686]">Vehicle Expenses</h2>}
        { openedFromList && (
          <button
            onClick={onBack}
            className="text-sm font-medium text-[#293686] border border-[#D8E0FF] bg-white hover:bg-[#F3F6FF] px-3 py-1.5 rounded-md transition-colors"
          >
            Back to List
          </button>
        )}
      </div>

      <div className="bg-white border border-[#D8E0FF] rounded-xl shadow-sm p-4 mb-4">
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div className="flex flex-col">
            <label className="text-xs text-gray-600 mb-1">Branch</label>
            {showBranches ? (
              <select
                value={selectedBranch}
                onChange={onBranchChange}
                className="border rounded px-3 py-2 text-sm"
              >
                {branches.map((b) => (
                  <option key={b.id} value={b.id}>
                    {b.name}
                  </option>
                ))}
              </select>
            ) : (
              <input
                type="text"
                readOnly
                value={branchLabel}
                className="border rounded px-3 py-2 text-sm bg-gray-50 text-gray-700"
              />
            )}
          </div>

          <div className="flex flex-col">
            <label className="text-xs text-gray-600 mb-1">Vehicle</label>
            <select
              value={selectedVehicle || ''}
              onChange={(e) => setSelectedVehicle(e.target.value)}
              className="border rounded px-3 py-2 text-sm"
            >
              {vehicles.map((v) => (
                <option key={v.id} value={v.id}>
                  {v.vehicle_number}
                </option>
              ))}
            </select>
          </div>
        </div>
      </div>

      <VehicleExpenseFilters
        selectedMonth={month}
        selectedYear={year}
        selectedStatus={status}
        onMonthChange={setMonth}
        onYearChange={setYear}
        onStatusChange={setStatus}
        onAdd={handleAdd}
        activeTab={activeTab}
        addDisabled={!canAddExpense}
      />

      {/* Tabs */}
      <div className="border-b mb-4 flex bg-white rounded-t-lg px-2">
        <button
          className={`px-4 py-2 ${activeTab === 'expenses' ? 'border-b-2 border-[#293686] text-[#293686] font-semibold' : 'text-gray-600'}`}
          onClick={() => setActiveTab('expenses')}
        >
          Expenses ({expenseCount})
        </button>
        <button
          className={`px-4 py-2 ${activeTab === 'approvals' ? 'border-b-2 border-[#293686] text-[#293686] font-semibold' : 'text-gray-600'}`}
          onClick={() => setActiveTab('approvals')}
        >
          Approvals ({approvalCount})
        </button>
      </div>

      {activeTab === 'expenses' ? (
        <VehicleExpenseTable
          month={month}
          year={year}
          status={status}
          selectedVehicle={selectedVehicle}
          onCountUpdate={updateCount}
          onEdit={handleEdit}
          onDelete={handleDelete}
          reload={reload}
        />
      ) : (
        <VehicleExpenseApprovalTable
          month={month}
          year={year}
          status={status}
          selectedVehicle={selectedVehicle}
          onCountUpdate={updateCount}
          onApprove={handleApprove}
          onReject={handleReject}
          reload={reload}
        />
      )}
      {showModal && (
        <VehicleExpenseModal
          vehicle={selectedVehicle}
          initialData={editingExpense}
          onClose={() => setShowModal(false)}
          onSave={handleSaved}
          dataErrors={errors}
        />
      )}

      
    </div>
  );
};

export default VehicleExpenses;
